import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSearch, FiCheck, FiBox } from 'react-icons/fi';
import { type ApiInventoryImportCandidate } from '../../../api/inventory';

interface ImportAccountInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  importSearchQuery: string;
  setImportSearchQuery: (val: string) => void;
  selectedImportIds: string[];
  setSelectedImportIds: React.Dispatch<React.SetStateAction<string[]>>;
  filteredImportItems: ApiInventoryImportCandidate[];
  toggleImportSelection: (uuid: string) => void;
  handleImportSelectedItems: () => void;
  isLoading?: boolean;
  isSubmitting?: boolean;
}

export const ImportAccountInventoryModal: React.FC<ImportAccountInventoryModalProps> = ({
  isOpen,
  onClose,
  importSearchQuery,
  setImportSearchQuery,
  selectedImportIds,
  setSelectedImportIds,
  filteredImportItems,
  toggleImportSelection,
  handleImportSelectedItems,
  isLoading = false,
  isSubmitting = false,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={isSubmitting ? undefined : onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-lg bg-[#0F0F0F] border border-white/10 rounded-[32px] overflow-hidden z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
              <div>
                <h3 className="text-base font-bold text-white">Import from Account Inventory</h3>
                <p className="text-zinc-500 text-xs mt-0.5">
                  Select items from your Musicade inventory to add to this project.
                </p>
              </div>
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="p-2 rounded-full hover:bg-white/5 text-zinc-400 hover:text-white cursor-pointer transition-colors disabled:opacity-50"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <FiSearch className="w-4 h-4 text-zinc-500 absolute left-4 top-3.5" />
                <input
                  type="text"
                  placeholder="Search your inventory..."
                  value={importSearchQuery}
                  onChange={(e) => setImportSearchQuery(e.target.value)}
                  disabled={isLoading || isSubmitting}
                  className="w-full pl-11 pr-4 py-3 bg-[#141414] border border-white/10 rounded-2xl text-xs text-white outline-none focus:border-blue-500 transition-all placeholder:text-zinc-600 disabled:opacity-50"
                />
              </div>

              {/* Selection Counter Bar */}
              {selectedImportIds.length > 0 && (
                <div className="bg-[#0B2545]/90 border border-[#1E40AF]/40 rounded-xl px-4 py-2.5 flex items-center justify-between text-xs">
                  <span className="text-[#60A5FA] font-semibold">
                    {selectedImportIds.length} {selectedImportIds.length === 1 ? 'item' : 'items'}{' '}
                    selected
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedImportIds([])}
                    disabled={isSubmitting}
                    className="text-[#60A5FA] hover:text-blue-300 font-semibold cursor-pointer disabled:opacity-50"
                  >
                    Clear
                  </button>
                </div>
              )}

              {/* Items List */}
              <div className="space-y-3 max-h-[320px] overflow-y-auto no-scrollbar pr-1">
                {isLoading ? (
                  /* Loading skeletons */
                  [1, 2, 3].map((n) => (
                    <div
                      key={n}
                      className="bg-[#141414] border border-white/5 rounded-2xl p-4 flex items-center gap-3.5 animate-pulse"
                    >
                      <div className="w-5 h-5 rounded-full bg-zinc-800" />
                      <div className="space-y-2 flex-1">
                        <div className="h-4 bg-zinc-800 rounded w-1/3" />
                        <div className="h-3 bg-zinc-800 rounded w-1/2" />
                      </div>
                    </div>
                  ))
                ) : filteredImportItems.length === 0 ? (
                  /* Empty state */
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-12 h-12 rounded-full bg-zinc-900 flex items-center justify-center text-zinc-600 mb-3">
                      <FiBox size={22} />
                    </div>
                    <p className="text-white text-sm font-semibold">
                      No inventory available to import.
                    </p>
                  </div>
                ) : (
                  filteredImportItems.map((item) => {
                    const isSelected = selectedImportIds.includes(item.uuid);
                    const isAlreadyImported = item.already_imported;
                    const isSelectable = item.selectable && !isAlreadyImported;

                    return (
                      <div
                        key={item.uuid}
                        onClick={() => {
                          if (!isSelectable || isSubmitting) return;
                          toggleImportSelection(item.uuid);
                        }}
                        className={`rounded-2xl p-4 flex items-center gap-3.5 transition-all ${
                          isAlreadyImported
                            ? 'opacity-40 cursor-not-allowed bg-[#141414]/50 border border-white/5'
                            : isSelected
                              ? 'bg-[#0B2545]/40 border border-[#2563EB] cursor-pointer'
                              : 'bg-[#141414] border border-white/5 hover:border-white/10 cursor-pointer'
                        }`}
                      >
                        {/* Checkbox or Imported Status Badge */}
                        <div className="shrink-0">
                          {isAlreadyImported ? (
                            <span className="text-[10px] bg-white/5 border border-white/10 text-zinc-400 px-2 py-0.5 rounded-full font-semibold">
                              Imported
                            </span>
                          ) : isSelected ? (
                            <div className="w-5 h-5 rounded-full bg-[#3B82F6] flex items-center justify-center text-white">
                              <FiCheck className="w-3.5 h-3.5" />
                            </div>
                          ) : (
                            <div className="w-5 h-5 rounded-full border border-white/20 bg-zinc-900" />
                          )}
                        </div>

                        {/* Content */}
                        <div>
                          <h4 className="text-white text-sm font-semibold">{item.name}</h4>
                          <p className="text-zinc-400 text-xs mt-0.5 flex items-center gap-1.5">
                            <span>{item.category_label || item.category}</span>
                            <span>·</span>
                            <span>Qty: {item.quantity}</span>
                            <span>·</span>
                            <span className="inline-flex items-center gap-1 text-[#34d399]">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#34d399]" />
                              {item.condition_label || item.condition}
                            </span>
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="flex items-center justify-end pt-4 border-t border-white/5">
                <button
                  type="button"
                  onClick={handleImportSelectedItems}
                  disabled={selectedImportIds.length === 0 || isSubmitting}
                  className="px-6 py-2.5 bg-white text-black hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-full text-xs font-bold transition-all cursor-pointer active:scale-95 shadow-md flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-3 h-3 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                      <span>Importing...</span>
                    </>
                  ) : (
                    <span>
                      Import{' '}
                      {selectedImportIds.length > 0
                        ? `${selectedImportIds.length} ${selectedImportIds.length === 1 ? 'Item' : 'Items'}`
                        : 'Items'}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
